import { ActionTypes } from '../types';

export function combineHiddenAssets(conditions, switchFlowConditions) {
  const tagsSet = new Set();
  const polygonsSet = new Set();
  const vsItemsSet = new Set();
  const vsObjectsSet = new Set();
  const sweepsSet = new Set();

  conditions.forEach((c) => {
    c.tags.forEach((tag) => tagsSet.add(tag));
    c.polygons.forEach((polygon) => polygonsSet.add(polygon));
    c.vsItems.forEach((item) => vsItemsSet.add(item));
    c.vsObjects.forEach((obj) => vsObjectsSet.add(obj));

    if (c.sweeps !== null) {
      sweepsSet.add(c.sweeps);
    }
  });

  const filteredTags = Array.from(tagsSet).filter((tag) => !switchFlowConditions?.[0]?.tags?.includes(tag));
  const filteredPolygons = Array.from(polygonsSet).filter((polygon) => !switchFlowConditions?.[0]?.polygons?.includes(polygon));
  const filteredVsItems = Array.from(vsItemsSet).filter((item) => !switchFlowConditions?.[0]?.vsItems?.includes(item));
  const filteredVsObjects = Array.from(vsObjectsSet).filter((obj) => !switchFlowConditions?.[0]?.vsObjects?.includes(obj));
  const filteredSweeps = Array.from(sweepsSet).filter((sweep) => !switchFlowConditions?.[0]?.sweeps?.includes(sweep));

  const returnCondition = [
    ...(!!filteredTags?.length || !!filteredPolygons?.length || !!filteredVsItems?.length || !!filteredVsObjects?.length
      ? [
          {
            action: ActionTypes.SHOW_ASSET,
            tags: filteredTags,
            polygons: filteredPolygons,
            vsItems: filteredVsItems,
            vsObjects: filteredVsObjects,
          },
        ]
      : []),
    ...(filteredSweeps?.length > 0
      ? [
          {
            action: ActionTypes.SHOW_SWEEP,
            sweeps: filteredSweeps,
          },
        ]
      : []),
  ];

  return returnCondition;
}

function handleAssetChanges(assetType, toShowSet, toHideSet, firstCondition, currentCondition) {
  if (currentCondition[assetType] && currentCondition[assetType].length > 0) {
    currentCondition[assetType].forEach((asset) => {
      if (currentCondition.action === 'show_asset') {
        toShowSet.delete(asset);

        // Add to `hide` only if it's not already in the first condition's 'show_asset'
        if (!firstCondition.isStartStep && firstCondition?.isStartCondition && firstCondition.id === currentCondition.id) {
          return;
        }
        if (firstCondition.isStartStep && firstCondition[assetType].includes(asset)) {
          toHideSet.add(asset);
          return;
        }
        toHideSet.add(asset);
      } else if (currentCondition.action === 'hide_asset') {
        toHideSet.delete(asset);

        // Add to `show` only if it's not already in the first condition's 'hide_asset'
        if (!firstCondition.isStartStep && firstCondition?.isStartCondition && firstCondition.id === currentCondition.id) {
          return;
        }

        if (firstCondition.isStartStep && firstCondition[assetType].includes(asset)) {
          toHideSet.add(asset);
          return;
        }

        toShowSet.add(asset);
      }
    });
  }
}

export const revertConditionsByStep = (stepConditionsMap, fromStep, toStep, startStepEdge) => {
  const tagsToShow = new Set();
  const tagsToHide = new Set();
  const vsItemsToShow = new Set();
  const vsItemsToHide = new Set();
  const vsObjectsToShow = new Set();
  const vsObjectsToHide = new Set();
  const sweepsToShow = new Set();
  const sweepsToHide = new Set();
  const polygonsToShow = new Set();
  const polygonsToHide = new Set();
  const sortedSteps = [...stepConditionsMap.keys()].filter((step) => step <= fromStep && step >= toStep).sort((a, b) => b - a);
  let firstExecutedConditionInToStep = stepConditionsMap.get(toStep)?.values()?.next()?.value;

  sortedSteps.forEach((step) => {
    const conditions = stepConditionsMap.get(step);
    if (startStepEdge.target === toStep) {
      // if toStep is step 1 we consider starting point the startStep
      firstExecutedConditionInToStep = stepConditionsMap.get(startStepEdge.source)?.values()?.next()?.value;
      if (!firstExecutedConditionInToStep) return;
      firstExecutedConditionInToStep.isStartCondition = true;
      firstExecutedConditionInToStep.isStartStep = true;
    }
    if (!conditions) return;
    Array.from(conditions)
      .reverse()
      .forEach((c: any) => {
        handleAssetChanges('tags', tagsToShow, tagsToHide, firstExecutedConditionInToStep, c);
        handleAssetChanges('polygons', polygonsToShow, polygonsToHide, firstExecutedConditionInToStep, c);
        handleAssetChanges('sweeps', sweepsToShow, sweepsToHide, firstExecutedConditionInToStep, c);
        handleAssetChanges('vsItems', vsItemsToShow, vsItemsToHide, firstExecutedConditionInToStep, c);
        handleAssetChanges('vsObjects', vsObjectsToShow, vsObjectsToHide, firstExecutedConditionInToStep, c);
      });
  });

  const returnConditions = [
    {
      action: ActionTypes.SHOW_ASSET,
      tags: Array.from(tagsToShow),
      polygons: Array.from(polygonsToShow),
      vsItems: Array.from(vsItemsToShow),
      vsObjects: Array.from(vsObjectsToShow),
    },
    {
      action: ActionTypes.SHOW_SWEEP,
      sweeps: Array.from(sweepsToShow),
    },
    {
      action: ActionTypes.HIDE_ASSET,
      tags: Array.from(tagsToHide),
      polygons: Array.from(polygonsToHide),
      vsItems: Array.from(vsItemsToHide),
      vsObjects: Array.from(vsObjectsToHide),
    },
    {
      action: ActionTypes.HIDE_SWEEP,
      sweeps: Array.from(sweepsToHide),
    },
  ].filter((c) => !!c?.tags?.length || !!c?.polygons?.length || !!c?.vsItems?.length || !!c?.vsObjects?.length || !!c?.sweeps?.length);

  return returnConditions;
};

const longestPathIncludingStep = (allPaths, currentStepId) => {
  const pathsWithCurrentStep = allPaths.filter((path) => path.includes(currentStepId));

  const longestPath = pathsWithCurrentStep.reduce((longest, current) => (current.length > longest.length ? current : longest), []);

  return longestPath;
};

export const calculateStepPositionInPath = (steps, stepsEdges, currentStep) => {
  if (!currentStep) return { currentStepPosition: 0, totalStepsInPath: 0 };

  const startingStep = steps.find((step) => step.type === 'start_step');
  if (!startingStep) {
    throw new Error('No starting step found');
  }

  const buildPath = (currentStepId, path = [], visited = new Set()) => {
    if (visited.has(currentStepId)) return [];
    visited.add(currentStepId);
    path.push(currentStepId);

    const outgoingEdges = stepsEdges.filter((edge) => edge.source === currentStepId);
    if (outgoingEdges.length === 0) return [path];

    const paths = outgoingEdges.flatMap((edge) => buildPath(edge.target, [...path], new Set(visited)));

    return paths;
  };

  const allPaths = buildPath(startingStep.id);

  const currentPath = longestPathIncludingStep(allPaths, currentStep.id);
  if (!currentPath) {
    throw new Error('Current step is not part of any path');
  }

  const filteredPath = currentPath.filter((stepId) => !['start_step', 'finish_step'].includes(steps.find((step) => step.id === stepId)?.type));

  const currentStepPosition = filteredPath.indexOf(currentStep.id) + 1;

  return {
    currentStepPosition,
    totalStepsInPath: filteredPath.length,
  };
};

export const calculatePathLengths = (steps, stepsEdges) => {
  const startingStep = steps.find((step) => step.type === 'start_step');
  const finishingStep = steps.find((step) => step.type === 'finish_step');

  if (!startingStep || !finishingStep) {
    throw new Error('Starting or finishing step not found');
  }

  const buildPaths = (currentStepId, path = [], visited = new Set()) => {
    if (visited.has(currentStepId)) return []; // 🚀 Prevent infinite recursion
    visited.add(currentStepId);

    path.push(currentStepId);

    const outgoingEdges = stepsEdges.filter((edge) => edge.source === currentStepId);
    if (outgoingEdges.length === 0) return [path];

    const paths = outgoingEdges.flatMap((edge) => buildPaths(edge.target, [...path], new Set(visited)));

    return paths;
  };

  const allPaths = buildPaths(startingStep.id);

  const validPaths = allPaths.filter((path) => path[path.length - 1] === finishingStep.id);

  const pathLengths = validPaths.map((path) => path.filter((stepId) => !['start_step', 'finish_step'].includes(steps.find((step) => step.id === stepId)?.type)).length);

  if (pathLengths.length === 0) {
    return { shortestPathLength: 0, longestPathLength: 0 };
  }

  return {
    shortestPathLength: Math.min(...pathLengths),
    longestPathLength: Math.max(...pathLengths),
  };
};
